import { useContext, useState } from 'react'
import { CutterContext } from '../../contexts/CutterContext'
import { InternalContext } from '../../contexts/InternalContext'
import { useElementEventListener } from '../../../hooks/useElementEventListener'
import { useChannelsElementRect } from './useChannelsElementRect'
import { useGetVisibleRatioPosition } from '../../hooks/useGetVisibleRatioPosition'
import { getPositionRange } from '../../../functions/getPositionRange'
import { useDocumentEventListener } from '../../../hooks/useDocumentEventListener'
import { Position, PositionRange } from '../../../types'
import clamp from 'lodash/clamp'
import { InterfaceEvent } from '../../../interfaces/InterfaceEvent'

export function useChannelsMouseSelecting() {
  const { setSelection, audio } = useContext(CutterContext)
  const {
    backgroundElement,
    mouseSelection,
    setMouseSelection,
    playbackCursorPosition,
    setPlaybackCursorPosition,
    autoplayEnabled,
    audioPlayer: { play, playback },
  } = useContext(InternalContext)
  const { width, left } = useChannelsElementRect()
  const [initialState, setInitialState] = useState<{ position: Position; clientX: number } | null>(null)
  const getVisibleRatioPosition = useGetVisibleRatioPosition()

  const onMouseDown = (event: MouseEvent) => {
    const position = getVisibleRatioPosition((event.clientX - left) / width)

    if (playbackCursorPosition && event.shiftKey) {
      setPlaybackCursorPosition(null)
      setSelection(getPositionRange(playbackCursorPosition, position))
    } else {
      setInitialState({ position, clientX: event.clientX })
      setMouseSelection([position, position])
    }
  }

  const onMouseMove = (event: MouseEvent) => {
    const position = getVisibleRatioPosition((event.clientX - left) / width)

    if (initialState) {
      setMouseSelection(getPositionRange(initialState.position, clamp(position, 0, audio.length)))
    }
  }

  const onClick = (mouseSelection: PositionRange) => {
    setPlaybackCursorPosition(mouseSelection[0])

    if (playback) {
      play(mouseSelection[0])
    } else if (autoplayEnabled) {
      play(mouseSelection[0])
    }
  }

  const onMouseUp = ({ clientX }: InterfaceEvent) => {
    if (mouseSelection && initialState) {
      if (Math.abs(clientX - initialState.clientX) < 2) {
        onClick(mouseSelection)
      } else {
        setSelection(mouseSelection)

        if (autoplayEnabled) {
          play(mouseSelection)
        }
      }
    }

    setMouseSelection(null)
  }

  useElementEventListener(backgroundElement, 'mousedown', onMouseDown)

  useDocumentEventListener('mousemove', onMouseMove, mouseSelection !== null)
  useDocumentEventListener('mouseup', onMouseUp, mouseSelection !== null)
}
